/**
 * CertificatesList — inventario de certificados TLS + ficha a pantalla completa.
 *
 * Mismo patrón que el resto de listados: `ListPage` y, al pulsar una fila, la
 * ficha (`CertificateForm`). La caducidad manda: columna con días restantes y
 * fila naranja a ≤30 días / roja si está vencido, igual que en DNS.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, PlusIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import ListPage from './common/ListPage';
import CertificateForm from './CertificateForm';
import { usuarioActual } from './feedbackConfig';

const ESTADOS = [
  { value: 'valid', label: 'Vigente' },
  { value: 'expiring', label: 'Caduca pronto' },
  { value: 'expired', label: 'Caducado' },
  { value: 'unknown', label: 'Sin fecha' },
];

function vencimiento(fecha) {
  if (!fecha) return null;
  const dias = Math.ceil((new Date(`${fecha}T23:59:59`) - Date.now()) / 86400000);
  return { dias, tono: dias < 0 ? 'bad' : dias <= 30 ? 'warn' : 'ok' };
}

export default function CertificatesList({ companyId }) {
  const superadmin = useMemo(() => usuarioActual().superadmin, []);
  const [items, setItems] = useState([]);
  const [dominios, setDominios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [ficha, setFicha] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getCertificates(companyId);
      setItems(res.data || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los certificados');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!companyId) return;
    api.getDnsDomains(companyId)
      .then((res) => setDominios(res.data || []))
      .catch(() => setDominios([]));
  }, [companyId]);

  const mostrarAviso = (texto) => {
    setAviso(texto);
    setTimeout(() => setAviso(''), 6000);
  };

  const sincronizar = async () => {
    setSincronizando(true);
    setError('');
    try {
      const res = await api.syncCertificates();
      await cargar();
      mostrarAviso(
        `OVH: ${res.created} nuevos · ${res.updated} actualizados · ${res.skipped} sin dominio en la CMDB`,
      );
    } catch (err) {
      setError(err.message || 'No se pudieron sincronizar los certificados de OVH');
    } finally {
      setSincronizando(false);
    }
  };

  const filtrados = useMemo(() => {    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      const coincide = !q
        || (item.name || '').toLowerCase().includes(q)
        || (item.common_name || '').toLowerCase().includes(q)
        || (item.issuer || '').toLowerCase().includes(q)
        || (item.domain_name || '').toLowerCase().includes(q);
      const estado = !filtroEstado || item.status === filtroEstado;
      return coincide && estado;
    });
  }, [items, search, filtroEstado]);

  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: 'Certificado',
        sortable: true,
        render: (item) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <ShieldCheckIcon style={{ width: 15, height: 15, color: 'var(--accent-l)' }} />
            <strong>{item.name}</strong>
          </span>
        ),
      },
      { key: 'common_name', label: 'CN', sortable: true, render: (item) => item.common_name || dash },
      { key: 'domain_name', label: 'Dominio', sortable: true, render: (item) => item.domain_name || dash },
      { key: 'issuer', label: 'Emitido por', sortable: true, render: (item) => item.issuer || dash },
      { key: 'cert_type_label', label: 'Tipo', sortable: true, render: (item) => item.cert_type_label || dash },
      {
        key: 'expires_at',
        label: 'Caduca',
        sortable: true,
        render: (item) => {
          const v = vencimiento(item.expires_at);
          if (!v) return dash;
          const clase = v.tono === 'bad' ? 'list-badge--off' : v.tono === 'warn' ? 'list-badge--warn' : 'list-badge--muted';
          return (
            <span className={`list-badge ${clase}`} title={item.auto_renew ? 'Renovación automática' : 'Renovación manual'}>
              {item.expires_at} · {v.dias < 0 ? `vencido ${Math.abs(v.dias)} d` : `${v.dias} d`}
            </span>
          );
        },
      },
      {
        key: 'status',
        label: 'Estado',
        sortable: true,
        render: (item) => (
          <span className={`list-badge ${
            item.status === 'expired' ? 'list-badge--off'
              : item.status === 'expiring' ? 'list-badge--warn'
                : item.status === 'valid' ? 'list-badge--on' : 'list-badge--muted'
          }`}>
            {item.status_label || item.status}
          </span>
        ),
      },
    ];
  }, []);

  const exportRows = useMemo(
    () => filtrados.map((item) => {
      const v = vencimiento(item.expires_at);
      return {
        Certificado: item.name,
        CN: item.common_name || '',
        Dominio: item.domain_name || '',
        Emitido_por: item.issuer || '',
        Tipo: item.cert_type_label || '',
        Caduca: item.expires_at || '',
        'Días restantes': v ? v.dias : '',
        'Renovación automática': item.auto_renew ? 'Sí' : 'No',
        Estado: item.status_label || item.status,
      };
    }),
    [filtrados],
  );

  const rowClassName = (item) => {
    const v = vencimiento(item.expires_at);
    if (!v) return '';
    if (v.dias < 0) return 'list-row-danger';
    if (v.dias <= 30) return 'list-row-warn';
    return '';
  };

  if (ficha) {
    return (
      <CertificateForm
        certificate={ficha.certificate}
        mode={ficha.mode}
        companyId={companyId}
        domains={dominios}
        siblings={filtrados}
        isSuperadmin={superadmin}
        onClose={() => setFicha(null)}
        onSaved={cargar}
        onDeleted={() => { setFicha(null); cargar(); }}
        onNavigate={(fila) => setFicha({ certificate: fila, mode: 'view' })}
      />
    );
  }

  return (
    <>
      <ListPage
        icon={ShieldCheckIcon}
      title="Certificados"
      subtitle="Certificados TLS del inventario y su caducidad. Aviso en el panel un mes antes."
      companyId={companyId}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Buscar certificado, CN, emisor o dominio…"
      headerActions={superadmin ? (
        <button
          type="button"
          className="list-btn-secondary"
          onClick={sincronizar}
          disabled={sincronizando}
          title="Traer a la CMDB los certificados SSL de la cuenta OVH (solo lectura)"
        >
          <ArrowPathIcon style={{ width: 15, height: 15 }} />
          {sincronizando ? 'Sincronizando…' : 'Sincronizar con OVH'}
        </button>
      ) : null}
      filters={[
        {
          key: 'status',
          label: 'Estado',
          allLabel: 'Todos los estados',
          value: filtroEstado,
          onChange: setFiltroEstado,
          options: ESTADOS,
        },
      ]}
      columns={columns}
      columnStorageKey="cmdb_cols_certificates"
      listKey="certificates"
      rows={filtrados}
      rowClassName={rowClassName}
      onRowClick={(item) => setFicha({ certificate: item, mode: 'view' })}
      exportRows={exportRows}
      exportFileBase="certificados"
      loading={loading && items.length === 0}
      error={error}
      onCreate={() => setFicha({ certificate: null, mode: 'create' })}
      createLabel="Nuevo certificado"
      createDisabled={!companyId || companyId === 'all'}
      createTitle={companyId && companyId !== 'all'
        ? 'Dar de alta un certificado'
        : 'Selecciona una empresa en la barra superior para dar de alta certificados'}
      empty={{
        icon: '🔐',
        title: items.length === 0 ? 'Todavía no hay certificados' : 'Ningún certificado coincide',
        subtitle: items.length === 0
          ? 'Da de alta el certificado con su emisor y su caducidad: el panel avisará un mes antes.'
          : undefined,
        action: items.length === 0 ? (
          <button
            type="button"
            className="list-btn-primary"
            onClick={() => setFicha({ certificate: null, mode: 'create' })}
            disabled={!companyId || companyId === 'all'}
          >
            <PlusIcon style={{ width: 14, height: 14 }} /> Nuevo certificado
          </button>
        ) : undefined,
      }}
    />
      {aviso && (
        <div className="list-badge list-badge--accent" style={{ position: 'fixed', bottom: 18, right: 18, zIndex: 1100 }}>
          {aviso}
        </div>
      )}
    </>
  );
}
